export const API_URL = import.meta.env.VITE_API_URL || 'https://protectcord-dashboard-api-production.sneakyhub-com.workers.dev';

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

class ApiClient {
  private baseURL: string;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async refreshToken(): Promise<boolean> {
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };

        // Send refresh token in header if available
        if (refreshToken) {
          headers['Authorization'] = `Bearer ${refreshToken}`;
        }

        const response = await fetch(`${this.baseURL}/token/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers,
        });

        if (response.ok) {
          // Check if response contains new tokens
          const data = await response.json().catch(() => ({}));
          if (data.access_token) {
            localStorage.setItem('access_token', data.access_token);
          }
          if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
          }
          return true;
        }
        return false;
      } catch (error) {
        console.error('Token refresh failed:', error);
        return false;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  async request<T = unknown>(
    endpoint: string,
    options: RequestOptions = {},
    retryOnUnauthorized: boolean = true
  ): Promise<T> {
    const { skipAuth, ...fetchOptions } = options;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(fetchOptions.headers as Record<string, string>),
    };

    // Add Authorization header from localStorage if available
    if (!skipAuth) {
      const accessToken = localStorage.getItem('access_token');
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
    }

    // Add Accept-Language header for i18n
    try {
      const langStorage = localStorage.getItem('language-storage');
      if (langStorage) {
        const parsed = JSON.parse(langStorage);
        if (parsed.state?.language) {
          headers['Accept-Language'] = parsed.state.language;
        }
      }
    } catch {
      // Ignore errors
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, {
      ...fetchOptions,
      headers,
      credentials: 'include', // Also send cookies as fallback
    });

    // Handle 401 Unauthorized - attempt token refresh
    if (
      response.status === 401 &&
      retryOnUnauthorized &&
      !skipAuth &&
      !endpoint.includes('/token/')
    ) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        // Retry with new token
        return this.request<T>(endpoint, options, false);
      }
      // Refresh failed - dispatch logout event
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      if (typeof errorData.error === 'string') {
        errorMessage = errorData.error;
      } else if (errorData.error?.issues && Array.isArray(errorData.error.issues)) {
        errorMessage = errorData.error.issues
          .map((i: { message: string }) => i.message)
          .join(', ');
      }

      const error = new Error(errorMessage) as Error & { status: number; code?: string };
      error.status = response.status;
      error.code = errorData.code;
      throw error;
    }

    return response.json();
  }

  // Auth endpoints
  async login(email: string, password: string, turnstileToken?: string) {
    return this.request<{
      success: boolean;
      token?: string;
      refresh_token?: string;
      user?: User;
      requires_2fa?: boolean;
      user_id?: number;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        turnstile_token: turnstileToken,
      }),
    });
  }

  async register(
    name: string,
    email: string,
    password: string,
    turnstileToken?: string
  ) {
    return this.request<{
      success: boolean;
      token: string;
      refresh_token: string;
      user: User;
    }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        name,
        email,
        password,
        turnstile_token: turnstileToken,
      }),
    });
  }

  async verify2FA(userId: number, code: string) {
    return this.request<{
      success: boolean;
      token: string;
      refresh_token: string;
      user: User;
    }>('/auth/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, code }),
    });
  }

  async logout() {
    return this.request<{ success: boolean }>('/auth/logout', {
      method: 'POST',
    });
  }

  async getMe() {
    return this.request<{
      success: boolean;
      user: User;
    }>('/auth/me');
  }

  async updateProfile(data: { name?: string; preferred_language?: string }) {
    return this.request<{ success: boolean }>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async requestPasswordReset(email: string) {
    return this.request<{ success: boolean }>('/auth/password/reset-request', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    });
  }

  async resetPassword(token: string, password: string) {
    return this.request<{ success: boolean }>('/auth/password/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
      skipAuth: true,
    });
  }

  // Guild endpoints
  async getGuilds(params?: { mine?: boolean }) {
    const query = new URLSearchParams();
    if (params?.mine) query.append('mine', 'true');
    const queryStr = query.toString();

    return this.request<{
      success: boolean;
      guilds: Guild[];
      is_admin: boolean;
      showing_all: boolean;
    }>(`/guilds${queryStr ? `?${queryStr}` : ''}`);
  }

  async getGuild(guildId: string) {
    return this.request<{
      success: boolean;
      guild: Guild;
    }>(`/guilds/${guildId}`);
  }

  async getGuildStats(guildId: string) {
    return this.request<{
      success: boolean;
      stats: GuildStats;
    }>(`/guilds/${guildId}/stats`);
  }

  async getGuildSecurity(guildId: string) {
    return this.request<{
      success: boolean;
      security: SecuritySettings;
    }>(`/guilds/${guildId}/security`);
  }

  async updateGuildSecurity(guildId: string, settings: Partial<SecuritySettings>) {
    return this.request<{ success: boolean }>(`/guilds/${guildId}/security`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getGuildRaid(guildId: string) {
    return this.request<{
      success: boolean;
      raid: RaidSettings;
    }>(`/guilds/${guildId}/raid`);
  }

  async updateGuildRaid(guildId: string, settings: Partial<RaidSettings>) {
    return this.request<{ success: boolean }>(`/guilds/${guildId}/raid`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getGuildAutomod(guildId: string) {
    return this.request<{
      success: boolean;
      automod: AutomodSettings;
    }>(`/guilds/${guildId}/automod`);
  }

  async updateGuildAutomod(guildId: string, settings: Partial<AutomodSettingsUpdate>) {
    return this.request<{ success: boolean }>(`/guilds/${guildId}/automod`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async getGuildMessages(guildId: string) {
    return this.request<{
      success: boolean;
      messages: CustomMessages;
    }>(`/guilds/${guildId}/messages`);
  }

  async updateGuildMessages(guildId: string, messages: Partial<CustomMessages>) {
    return this.request<{ success: boolean }>(`/guilds/${guildId}/messages`, {
      method: 'PUT',
      body: JSON.stringify(messages),
    });
  }

  async getGuildWebhooks(guildId: string) {
    return this.request<{
      success: boolean;
      webhooks: WebhookSettings;
    }>(`/guilds/${guildId}/webhooks`);
  }

  async updateGuildWebhooks(guildId: string, webhooks: Partial<WebhookSettingsUpdate>) {
    return this.request<{ success: boolean }>(`/guilds/${guildId}/webhooks`, {
      method: 'PUT',
      body: JSON.stringify(webhooks),
    });
  }

  async getGuildWelcomer(guildId: string) {
    return this.request<{
      success: boolean;
      welcomer: WelcomerSettings;
    }>(`/guilds/${guildId}/welcomer`);
  }

  async updateGuildWelcomer(guildId: string, settings: Partial<WelcomerSettingsUpdate>) {
    return this.request<{ success: boolean }>(`/guilds/${guildId}/welcomer`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async testWelcomerMessage(guildId: string) {
    return this.request<{ success: boolean; message?: string; error?: string }>(`/guilds/${guildId}/welcomer/test`, {
      method: 'POST',
    });
  }

  async getGuildGeneralSettings(guildId: string) {
    return this.request<{
      success: boolean;
      settings: GeneralSettings;
    }>(`/guilds/${guildId}/general-settings`);
  }

  async updateGuildGeneralSettings(guildId: string, settings: Partial<GeneralSettingsUpdate>) {
    return this.request<{ success: boolean }>(`/guilds/${guildId}/general-settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  async resetGuildSettings(guildId: string) {
    return this.request<{ success: boolean; message: string }>(`/guilds/${guildId}/reset-settings`, {
      method: 'POST',
    });
  }

  async clearVerificationHistory(guildId: string) {
    return this.request<{ success: boolean; message: string; cleared: number }>(`/guilds/${guildId}/verification-history`, {
      method: 'DELETE',
    });
  }

  async clearBlockHistory(guildId: string) {
    return this.request<{ success: boolean; message: string; cleared: number }>(`/guilds/${guildId}/block-history`, {
      method: 'DELETE',
    });
  }

  async getGuildSetup(guildId: string) {
    return this.request<{
      success: boolean;
      setup: SetupInfo;
    }>(`/guilds/${guildId}/setup`);
  }

  async getGuildDiscordChannels(guildId: string) {
    return this.request<{
      success: boolean;
      channels: DiscordChannel[];
    }>(`/guilds/${guildId}/discord/channels`);
  }

  async getGuildDiscordRoles(guildId: string) {
    return this.request<{
      success: boolean;
      roles: DiscordRole[];
    }>(`/guilds/${guildId}/discord/roles`);
  }

  // ============================================
  // ROLE MANAGEMENT
  // ============================================

  async getGuildJoinRoles(guildId: string) {
    return this.request<{
      success: boolean;
      roles: JoinRole[];
    }>(`/guilds/${guildId}/join-roles`);
  }

  async addGuildJoinRole(guildId: string, roleId: string) {
    return this.request<{ success: boolean }>(`/guilds/${guildId}/join-roles`, {
      method: 'POST',
      body: JSON.stringify({ role_id: roleId }),
    });
  }

  async removeGuildJoinRole(guildId: string, roleId: string) {
    return this.request<{ success: boolean }>(`/guilds/${guildId}/join-roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  async getGuildRolePanels(guildId: string) {
    return this.request<{
      success: boolean;
      panels: ButtonRolePanel[];
    }>(`/guilds/${guildId}/role-panels`);
  }

  async createGuildRolePanel(guildId: string, data: ButtonRolePanelCreate) {
    return this.request<{
      success: boolean;
      panelId: number;
      messageId: string;
    }>(`/guilds/${guildId}/role-panels`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateGuildRolePanel(guildId: string, panelId: number, data: Partial<ButtonRolePanelCreate>) {
    return this.request<{ success: boolean }>(`/guilds/${guildId}/role-panels/${panelId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteGuildRolePanel(guildId: string, panelId: number) {
    return this.request<{ success: boolean }>(`/guilds/${guildId}/role-panels/${panelId}`, {
      method: 'DELETE',
    });
  }

  async getGuildBlocks(guildId: string, limit = 50, offset = 0, days?: number) {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (days) params.set('days', String(days));
    return this.request<{
      success: boolean;
      blocks: SecurityBlock[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/guilds/${guildId}/blocks?${params}`);
  }

  async checkGuildIp(guildId: string, body: { ip?: string; discord_user_id?: string }) {
    return this.request<{
      success: boolean;
      result: IpCheckResult;
      error?: string;
    }>(`/guilds/${guildId}/check-ip`, { method: 'POST', body: JSON.stringify(body) });
  }

  async getGuildMembers(guildId: string, limit = 50, offset = 0) {
    return this.request<{
      success: boolean;
      members: VerifiedMember[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/guilds/${guildId}/members?limit=${limit}&offset=${offset}`);
  }

  async exportGuildSettings(guildId: string) {
    return this.request<{
      success: boolean;
      export: {
        version: number;
        exported_at: string;
        settings: Record<string, unknown>;
      };
    }>(`/guilds/${guildId}/export`);
  }

  async importGuildSettings(guildId: string, settings: Record<string, unknown>) {
    return this.request<{ success: boolean }>(`/guilds/${guildId}/import`, {
      method: 'POST',
      body: JSON.stringify({ version: 1, settings }),
    });
  }

  // Admin endpoints
  async getAdminStats() {
    return this.request<{
      success: boolean;
      stats: AdminStats;
    }>('/admin/stats');
  }

  async getAdminUsers(params?: { limit?: number; offset?: number; search?: string }) {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    if (params?.search) query.append('search', params.search);

    return this.request<{
      success: boolean;
      users: AdminUser[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/admin/users?${query.toString()}`);
  }

  async getAdminUser(userId: number) {
    return this.request<{
      success: boolean;
      user: AdminUserDetails;
    }>(`/admin/users/${userId}`);
  }

  async updateAdminUser(userId: number, data: { name?: string; email_verified?: number }) {
    return this.request<{ success: boolean }>(`/admin/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async impersonateUser(userId: number) {
    return this.request<{
      success: boolean;
      token: string;
      user: { id: number; name: string; email: string };
      expires_in: number;
    }>(`/admin/users/${userId}/impersonate`, { method: 'POST' });
  }

  async getAdminProducts() {
    return this.request<{ success: boolean; products: AdminProduct[] }>('/admin/products');
  }

  async getAdminPurchases(params?: { limit?: number; offset?: number; search?: string }) {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    if (params?.search) query.set('search', params.search);
    return this.request<{
      success: boolean;
      purchases: AdminPurchase[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/admin/purchases?${query}`);
  }

  async revokeAdminPurchase(purchaseId: number) {
    return this.request<{ success: boolean; message: string }>(`/admin/purchases/${purchaseId}/revoke`, {
      method: 'POST',
    });
  }

  async createAdminProduct(data: Partial<AdminProduct>) {
    return this.request<{ success: boolean; product_id: number }>('/admin/products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminProduct(id: number, data: Partial<AdminProduct>) {
    return this.request<{ success: boolean }>(`/admin/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAdminProduct(id: number) {
    return this.request<{ success: boolean }>(`/admin/products/${id}`, { method: 'DELETE' });
  }

  async getAdminGuilds(params?: { limit?: number; offset?: number; search?: string }) {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    if (params?.search) query.append('search', params.search);

    return this.request<{
      success: boolean;
      guilds: Guild[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/admin/guilds?${query.toString()}`);
  }

  async getAdminActivity(params?: {
    limit?: number;
    offset?: number;
    user_id?: number;
    action?: string;
    search?: string;
    from?: string;
    to?: string;
  }) {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    if (params?.user_id) query.append('user_id', params.user_id.toString());
    if (params?.action) query.append('action', params.action);
    if (params?.search) query.append('search', params.search);
    if (params?.from) query.append('from', params.from);
    if (params?.to) query.append('to', params.to);

    return this.request<{
      success: boolean;
      activity: ActivityLog[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/admin/activity?${query.toString()}`);
  }

  async downloadAdminActivityExport(params?: { user_id?: number; action?: string; search?: string; from?: string; to?: string }) {
    const query = new URLSearchParams();
    if (params?.user_id) query.append('user_id', params.user_id.toString());
    if (params?.action) query.append('action', params.action);
    if (params?.search) query.append('search', params.search);
    if (params?.from) query.append('from', params.from);
    if (params?.to) query.append('to', params.to);

    const token = localStorage.getItem('access_token') || '';
    const res = await fetch(`${this.baseURL}/admin/activity/export?${query.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `activity-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async getAdminRoles() {
    return this.request<{
      success: boolean;
      roles: Role[];
    }>('/admin/roles');
  }

  async createAdminRole(data: {
    name: string;
    display_name: string;
    permissions?: string[];
  }) {
    return this.request<{
      success: boolean;
      role: Role;
    }>('/admin/roles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async assignUserRole(userId: number, roleName: string, guildId?: string | null) {
    return this.request<{ success: boolean }>(`/admin/users/${userId}/roles`, {
      method: 'POST',
      body: JSON.stringify({ role_name: roleName, guild_id: guildId }),
    });
  }

  async removeUserRole(userId: number, roleName: string, guildId?: string) {
    const query = guildId ? `?guild_id=${guildId}` : '';
    return this.request<{ success: boolean }>(
      `/admin/users/${userId}/roles/${roleName}${query}`,
      { method: 'DELETE' }
    );
  }

  // Admin Settings endpoints
  async getAdminSettings() {
    return this.request<{
      success: boolean;
      settings: Record<string, Array<{ name: string; value: unknown; locked: boolean }>>;
    }>('/admin/settings');
  }

  async getAdminSettingsGroup(group: string) {
    return this.request<{
      success: boolean;
      group: string;
      settings: Record<string, { value: unknown; locked: boolean }>;
    }>(`/admin/settings/${group}`);
  }

  async updateAdminSettings(group: string, settings: Record<string, unknown>) {
    return this.request<{
      success: boolean;
      message: string;
      updated: string[];
    }>(`/admin/settings/${group}`, {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  }

  async seedAdminSettings() {
    return this.request<{
      success: boolean;
      message: string;
      seeded: number;
    }>('/admin/settings/seed', { method: 'POST' });
  }

  // Admin Health & Monitoring endpoints
  async getAdminHealth() {
    return this.request<{
      success: boolean;
      status: 'ok' | 'warning' | 'error';
      checks: Record<string, { status: 'ok' | 'warning' | 'error'; message: string }>;
      timestamp: string;
    }>('/admin/health');
  }

  async getAdminRecentDetections(limit: number = 20) {
    return this.request<{
      success: boolean;
      detections: SecurityDetection[];
    }>(`/admin/recent-detections?limit=${limit}`);
  }

  async getAdminApiUsageStats() {
    return this.request<{
      success: boolean;
      stats: {
        total_active_keys: number;
        usage_today: number;
        usage_week: number;
        top_endpoints: Array<{ endpoint: string; count: number }>;
        top_users: Array<{ user_id: string; request_count: number; total_usage: number }>;
      };
    }>('/admin/api-usage-stats');
  }

  // Admin Email endpoints
  async getEmailConfig() {
    return this.request<{
      success: boolean;
      config: {
        mailgun_enabled: boolean;
        mailgun_domain: string;
        from_name: string;
        from_email: string;
        has_api_key: boolean;
      };
    }>('/admin/email/config');
  }

  async sendTestEmail(recipient: string) {
    return this.request<{
      success: boolean;
      message: string;
      messageId?: string;
    }>('/admin/email/test', {
      method: 'POST',
      body: JSON.stringify({ recipient }),
    });
  }

  async getEmailLogs(params?: {
    limit?: number;
    offset?: number;
    status?: string;
    template?: string;
    recipient?: string;
  }) {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    if (params?.status) query.append('status', params.status);
    if (params?.template) query.append('template', params.template);
    if (params?.recipient) query.append('recipient', params.recipient);

    return this.request<{
      success: boolean;
      logs: EmailLog[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/admin/email/logs?${query.toString()}`);
  }

  async getEmailLog(id: number) {
    return this.request<{
      success: boolean;
      log: EmailLog;
    }>(`/admin/email/logs/${id}`);
  }

  async resendEmail(id: number) {
    return this.request<{
      success: boolean;
      message: string;
      messageId?: string;
    }>(`/admin/email/resend/${id}`, { method: 'POST' });
  }

  async getEmailStats() {
    return this.request<{
      success: boolean;
      stats: {
        total: number;
        sent: number;
        delivered: number;
        failed: number;
        bounced: number;
        pending_queue: number;
      };
    }>('/admin/email/stats');
  }

  async getEmailTemplates() {
    return this.request<{
      success: boolean;
      templates: EmailTemplate[];
    }>('/admin/email/templates');
  }

  async getEmailTemplate(slug: string) {
    return this.request<{
      success: boolean;
      template: EmailTemplate;
    }>(`/admin/email/templates/${slug}`);
  }

  async updateEmailTemplate(slug: string, data: Partial<EmailTemplate>) {
    return this.request<{ success: boolean; message: string }>(
      `/admin/email/templates/${slug}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      }
    );
  }

  async previewEmailTemplate(slug: string, variables?: Record<string, string>) {
    return this.request<{
      success: boolean;
      preview: {
        subject: string;
        html_body: string;
        text_body: string | null;
        variables: string[];
      };
    }>(`/admin/email/templates/${slug}/preview`, {
      method: 'POST',
      body: JSON.stringify({ variables }),
    });
  }

  async getEmailQueue(limit = 50, offset = 0, status?: string) {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (status) params.set('status', status);
    return this.request<{
      success: boolean;
      queue: EmailQueueItem[];
      pagination: { limit: number; offset: number };
    }>(`/admin/email/queue?${params}`);
  }

  async processEmailQueue() {
    return this.request<{
      success: boolean;
      processed: number;
      sent: number;
      failed: number;
    }>('/admin/email/process-queue', { method: 'POST' });
  }

  async refreshGuildMetadata() {
    return this.request<{
      success: boolean;
      processed: number;
      results: Array<{ guild_id: string; status: string; name?: string }>;
    }>('/admin/guilds/refresh-metadata', { method: 'POST' });
  }

  async grantGuildPremium(guildId: string, data: { tier?: string; expires_at?: string | null }) {
    return this.request<{ success: boolean; message: string }>(`/admin/guilds/${guildId}/premium`, {
      method: 'POST',
      body: JSON.stringify({ active: true, ...data }),
    });
  }

  async revokeGuildPremium(guildId: string) {
    return this.request<{ success: boolean; message: string }>(`/admin/guilds/${guildId}/premium`, {
      method: 'POST',
      body: JSON.stringify({ active: false }),
    });
  }

  // Public endpoints (no auth required)
  async getPublicStats() {
    return this.request<{
      success: boolean;
      stats: {
        servers: number;
        verified: number;
        blocked: number;
        api_requests: number;
        cached_ips?: number;
        api_keys?: number;
        requests_today?: number;
      };
    }>('/public/stats', { skipAuth: true });
  }

  async getPublicStatsDetailed() {
    return this.request<{
      success: boolean;
      stats: DetailedStats;
      message?: string;
    }>('/public/stats/detailed', { skipAuth: true });
  }

  async getMainApiStats() {
    const res = await fetch('https://api.protectcord.com/stats');
    if (!res.ok) throw new Error(`Stats API returned ${res.status}`);
    return res.json() as Promise<{
      usage_today: {
        total_requests: number;
        cached_hits: number;
        ipapi_free_requests: number;
        ipapi_paid_requests: number;
        getipintel_requests: number;
      };
      usage_all_time: {
        total_requests: number;
        total_cached_hits: number;
        total_free_requests: number;
        total_paid_requests: number;
      };
      api_keys_count: number;
      cached_ips: number;
      threat_intelligence: {
        total_unique_ips: number;
        total_in_cache: number;
        total_in_reputation_db: number;
        vpn: number;
        proxy: number;
        tor: number;
        datacenter: number;
        abuser: number;
        clean: number;
      };
      whitelist_count: number;
      blacklist_count: number;
      risk_levels: {
        low: number;
        medium: number;
        high: number;
        critical: number;
      };
      proxy_sources: {
        total: number;
        active: number;
        total_ips_processed: number;
        sources_with_success: number;
      };
      ip_management: {
        ip_whitelist: number;
        ip_blacklist: number;
        asn_whitelist: number;
        asn_blacklist: number;
      };
      timestamp: string;
    }>;
  }

  async getPublicSettings() {
    return this.request<{
      success: boolean;
      settings: PublicSettings;
    }>('/public/settings', { skipAuth: true });
  }

  // Translation endpoints
  async getTranslations(lang: string) {
    return this.request<Record<string, Record<string, string>>>(
      `/translations/${lang}`
    );
  }

  async getAvailableLanguages() {
    return this.request<{
      success: boolean;
      languages: Language[];
    }>('/translations');
  }

  // 2FA endpoints
  async setup2FA() {
    return this.request<{
      success: boolean;
      secret: string;
      otpauth: string;
    }>('/auth/2fa/setup', { method: 'POST' });
  }

  async enable2FA(code: string) {
    return this.request<{ success: boolean; message: string }>('/auth/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async disable2FA(code: string) {
    return this.request<{ success: boolean; message: string }>('/auth/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  // API Key endpoints
  async getApiKey() {
    return this.request<{
      success: boolean;
      api_key: {
        masked_key: string;
        usage_count: number;
        created_at: string;
        last_used_at: string | null;
        active: boolean;
      } | null;
      message?: string;
      requires_discord?: boolean;
    }>('/apikeys');
  }

  async generateApiKey() {
    return this.request<{
      success: boolean;
      api_key: string;
      message: string;
    }>('/apikeys', { method: 'POST' });
  }

  async revokeApiKey() {
    return this.request<{ success: boolean; message: string }>('/apikeys', {
      method: 'DELETE',
    });
  }

  async getApiKeyUsage() {
    return this.request<{
      success: boolean;
      usage: {
        your_requests: number;
        global_requests_today: number;
        cached_today: number;
        cached_all_time: number;
        recent_endpoints: Array<{ endpoint: string; count: number }>;
      } | null;
    }>('/apikeys/usage');
  }

  // Password endpoints
  async changePassword(currentPassword: string | undefined, newPassword: string) {
    return this.request<{ success: boolean; message: string }>('/auth/password/change', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword || undefined,
        new_password: newPassword,
      }),
    });
  }

  // OAuth provider endpoints
  async getLinkedProviders() {
    return this.request<{
      success: boolean;
      providers: { provider: string; provider_user_id: string; created_at: string }[];
    }>('/auth/providers');
  }

  async unlinkProvider(provider: string) {
    return this.request<{ success: boolean; message: string }>(`/auth/providers/${provider}`, {
      method: 'DELETE',
    });
  }

  async deleteAccount(password?: string) {
    return this.request<{ success: boolean; message: string }>('/auth/account', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    });
  }

  async getSessions() {
    return this.request<{ success: boolean; sessions: UserSession[] }>('/auth/sessions');
  }

  async revokeSession(sessionId: number) {
    return this.request<{ success: boolean; message: string }>(`/auth/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  async revokeAllSessions() {
    return this.request<{ success: boolean; message: string }>('/auth/sessions', {
      method: 'DELETE',
    });
  }

  async setEmail(email: string) {
    return this.request<{
      success: boolean;
      message: string;
      email: string;
    }>('/auth/email', {
      method: 'PUT',
      body: JSON.stringify({ email }),
    });
  }

  // Contribution endpoints
  async getContributionStats() {
    return this.request<{
      success: boolean;
      goal: ContributionGoal | null;
      recent_contributions: ContributionEntry[];
      stats: {
        total_contributors: number;
        total_amount: number;
        this_month_amount: number;
      };
    }>('/contribute/stats', { skipAuth: true });
  }

  async createContributionCheckout(data: {
    amount: number;
    is_anonymous?: boolean;
    display_name?: string;
    message?: string;
  }) {
    return this.request<{
      success: boolean;
      checkout_url: string;
      session_id: string;
    }>('/contribute/checkout', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async verifyContribution(sessionId: string) {
    return this.request<{
      success: boolean;
      payment_status: string;
      amount: number;
      display_name?: string;
    }>(`/contribute/verify/${sessionId}`, { skipAuth: true });
  }

  // Shop endpoints
  async getShopProducts(params?: { category?: string; featured?: boolean }) {
    const query = new URLSearchParams();
    if (params?.category) query.append('category', params.category);
    if (params?.featured) query.append('featured', 'true');

    return this.request<{
      success: boolean;
      products: ShopProduct[];
      categories: Record<string, ShopProduct[]>;
    }>(`/shop/products?${query.toString()}`, { skipAuth: true });
  }

  async getShopProduct(slug: string) {
    return this.request<{
      success: boolean;
      product: ShopProduct;
    }>(`/shop/products/${slug}`, { skipAuth: true });
  }

  async createShopCheckout(data: {
    product_slug: string;
    quantity?: number;
    guild_id?: string;
  }) {
    return this.request<{
      success: boolean;
      checkout_url: string;
      session_id: string;
    }>('/shop/checkout', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getShopOrders(params?: { limit?: number; offset?: number }) {
    const query = new URLSearchParams();
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());

    return this.request<{
      success: boolean;
      orders: ShopOrder[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/shop/orders?${query.toString()}`);
  }

  async getShopOrder(orderNumber: string) {
    return this.request<{
      success: boolean;
      order: ShopOrder & { items: ShopOrderItem[] };
    }>(`/shop/orders/${orderNumber}`);
  }

  async getUserPurchases() {
    return this.request<{
      success: boolean;
      purchases: UserPurchase[];
    }>('/shop/purchases');
  }

  async getEntitlements() {
    return this.request<{
      success: boolean;
      entitlements: Entitlements;
      active_slugs: string[];
    }>('/shop/entitlements');
  }

  async getUserStats() {
    return this.request<{
      success: boolean;
      stats: { total_servers: number; total_verified: number; total_blocks: number };
    }>('/guilds/user-stats');
  }

  async getNotificationPreferences() {
    return this.request<{ success: boolean; preferences: NotificationSettings }>('/auth/notification-preferences');
  }

  async updateNotificationPreferences(prefs: Partial<NotificationSettings>) {
    return this.request<{ success: boolean; preferences: NotificationSettings }>('/auth/notification-preferences', {
      method: 'PUT',
      body: JSON.stringify(prefs),
    });
  }

  async getUserActivity(limit = 25, offset = 0, action = '') {
    const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (action) params.set('action', action);
    return this.request<{
      success: boolean;
      activity: UserActivityLog[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/auth/activity?${params}`);
  }

  async createSubscriptionPortal() {
    return this.request<{
      success: boolean;
      portal_url: string;
    }>('/shop/subscriptions/portal', { method: 'POST' });
  }

  // Auth helper endpoints
  async resendVerificationEmail() {
    return this.request<{ success: boolean }>('/auth/resend-verification', {
      method: 'POST',
    });
  }


  // Admin languages
  async getAdminLanguages() {
    return this.request<{
      success: boolean;
      languages: AdminLanguage[];
    }>('/admin/languages', { method: 'GET' });
  }

  async createAdminLanguage(data: { code: string; name: string; native_name: string; enabled: number }) {
    return this.request<{ success: boolean }>('/admin/languages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Admin API keys
  async getAdminApiKeys(limit = 100, offset = 0) {
    return this.request<{
      success: boolean;
      api_keys: AdminApiKeyInfo[];
      pagination?: { total: number; limit: number; offset: number };
    }>(`/admin/api-keys?limit=${limit}&offset=${offset}`, { method: 'GET' });
  }

  async revokeAdminApiKey(userId: string) {
    return this.request<{ success: boolean }>(`/admin/api-keys/${userId}/revoke`, {
      method: 'POST',
    });
  }

  // Application API key management (admin)
  async getApplicationApiKeys() {
    return this.request<{ success: boolean; data: ApplicationApiKey[] }>('/admin/application-keys');
  }

  async createApplicationApiKey(payload: {
    name: string;
    description?: string;
    permissions: AppKeyPermissions;
    allowed_ips?: string;
  }) {
    return this.request<{
      success: boolean;
      data: ApplicationApiKey & { api_key: string };
      message: string;
    }>('/admin/application-keys', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateApplicationApiKey(
    id: number,
    payload: { name?: string; description?: string; permissions?: AppKeyPermissions; allowed_ips?: string | null }
  ) {
    return this.request<{ success: boolean; data: ApplicationApiKey }>(`/admin/application-keys/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteApplicationApiKey(id: number) {
    return this.request<{ success: boolean; message: string }>(`/admin/application-keys/${id}`, {
      method: 'DELETE',
    });
  }

  // Threat stats (public)
  async getThreatStats() {
    const res = await fetch('https://api.protectcord.com/threat-stats');
    if (!res.ok) throw new Error(`Threat stats API returned ${res.status}`);
    return res.json() as Promise<ThreatStatsResponse>;
  }

  // Verification endpoints (no auth required)
  async getVerification(code: string) {
    return this.request<{
      success: boolean;
      verification: VerificationInfo;
    }>(`/verify/${code}`, { skipAuth: true });
  }

  async submitVerification(code: string, turnstileToken: string) {
    return this.request<{
      success: boolean;
      message?: string;
      guild_name?: string;
      blocked?: boolean;
      error?: string;
    }>(`/verify/${code}`, {
      method: 'POST',
      body: JSON.stringify({ turnstile_token: turnstileToken }),
      skipAuth: true,
    });
  }

  // Game Management endpoints
  async getGamePlayers(limit = 25, offset = 0, search?: string, server?: string, gameType?: string) {
    const query = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    if (search) query.append('search', search);
    if (server) query.append('server', server);
    if (gameType) query.append('game_type', gameType);

    return this.request<{
      success: boolean;
      players: GamePlayerLog[];
      pagination: { total: number; limit: number; offset: number };
    }>(`/minecraft/players?${query.toString()}`);
  }

  async getGameStats() {
    return this.request<{
      success: boolean;
      stats: GameStats;
    }>('/minecraft/stats');
  }

  async getGameServers() {
    return this.request<{
      success: boolean;
      servers: GameServer[];
    }>('/minecraft/servers');
  }

  async getGamePlayer(uuid: string) {
    return this.request<{
      success: boolean;
      logs: GamePlayerLog[];
    }>(`/minecraft/player/${uuid}`);
  }
}

// Types
export interface GamePlayerLog {
  id: number;
  player_uuid: string;
  player_name: string;
  player_ip: string | null;
  server_name: string;
  game_type: string;
  action_taken: string;
  is_vpn: number;
  is_proxy: number;
  is_tor: number;
  is_datacenter: number;
  country_code: string | null;
  blocked: number;
  checked_at: string;
}

export interface GameStats {
  total_checks: number;
  unique_players: number;
  unique_servers: number;
  total_blocked: number;
  vpn_count: number;
  proxy_count: number;
  tor_count: number;
}

export interface GameServer {
  server_name: string;
  player_count: number;
  unique_players: number;
}

export interface PublicSettings {
  site_name: string;
  site_description: string;
  site_logo: string | null;
  maintenance_mode: boolean;
  maintenance_message: string;
  alert_enabled: boolean;
  alert_type: string;
  alert_message: string;
  turnstile_enabled: boolean;
  turnstile_site_key: string;
  // Google Analytics
  ga_enabled: boolean;
  ga_measurement_id: string;
  ga_debug_mode: boolean;
  ga_anonymize_ip: boolean;
  ga_track_page_views: boolean;
  ga_track_outbound_links: boolean;
}

export interface User {
  id: number;
  name: string;
  email: string | null;
  discord_id: string | null;
  avatar: string | null;
  two_factor_enabled: number;
  email_verified: number;
  preferred_language?: string;
  created_at?: string;
  has_password?: boolean;
  linked_providers?: string[];
  roles?: string[];
}

export interface Guild {
  guild_id: string;
  owner_id: string;
  owner_username?: string | null;
  guild_name: string | null;
  icon: string | null;
  member_count: number;
  setup_completed: boolean;
  bot_active: boolean | number | null;
  created_at: string;
  updated_at: string;
}

export interface GuildStats {
  verified_members: number;
  verified_today: number;
  verified_week: number;
  verified_month: number;
  total_blocks: number;
  recent_blocks_24h: number;
  blocks_week: number;
  blocks_month: number;
  breakdown: {
    vpn: number;
    proxy: number;
    tor: number;
  };
}

export interface SecuritySettings {
  vpn_blocking_enabled: boolean;
  proxy_blocking_enabled: boolean;
  tor_blocking_enabled: boolean;
  datacenter_blocking_enabled: boolean;
  regional_blocking_enabled: boolean;
  blocked_countries: string;
  allowed_countries: string;
  country_mode: 'blocklist' | 'allowlist';
  anti_alt_enabled: boolean;
  min_account_age_days: number;
  new_account_warning_days: number;
  risk_score_threshold: number;
  block_action: 'log_only' | 'kick' | 'ban';
  security_bypass_roles: string;
  suspicious_email_blocking: boolean;
}

export interface RaidSettings {
  raid_detection_enabled: boolean;
  raid_join_threshold: number;
  raid_time_window: number;
  raid_action: string;
  lockdown_duration: number;
  raid_alert_webhook: string | null;
  // Enhanced options
  raid_auto_disable_minutes: number;
  raid_min_account_age_days: number;
  raid_dm_users: boolean;
  raid_dm_message: string | null;
  raid_verification_mode: 'normal' | 'strict' | 'disabled';
  raid_exempt_roles: string;
  raid_slowmode_enabled: boolean;
  raid_slowmode_joins_per_minute: number;
  raid_log_channel_id: string | null;
  raid_pattern_detection: boolean;
  raid_notify_owner: boolean;
}

export type AutomodAction = 'delete' | 'warn' | 'timeout' | 'kick' | 'ban';

export interface AutomodSettings {
  automod_enabled: boolean;
  // Links (Discord KEYWORD rule)
  automod_links_enabled: boolean;
  automod_links_action: AutomodAction;
  automod_links_whitelist: string[];
  // Invites (Discord KEYWORD rule)
  automod_invites_enabled: boolean;
  automod_invites_action: AutomodAction;
  automod_invites_whitelist: string[];
  // Mentions (Discord MENTION_SPAM rule)
  automod_mentions_enabled: boolean;
  automod_mentions_limit: number;
  automod_mentions_action: AutomodAction;
  // Spam (Discord ML-based SPAM rule)
  automod_spam_enabled: boolean;
  automod_spam_action: AutomodAction;
  // Words (Discord KEYWORD rule)
  automod_words_enabled: boolean;
  automod_words_list: string[];
  automod_words_action: AutomodAction;
  automod_words_wildcard: boolean;
  // Zalgo (Discord KEYWORD rule with regex)
  automod_zalgo_enabled: boolean;
  automod_zalgo_action: AutomodAction;
  // Discord Content Filter (KEYWORD_PRESET rule)
  automod_preset_enabled: boolean;
  automod_preset_profanity: boolean;
  automod_preset_slurs: boolean;
  automod_preset_sexual: boolean;
  // Exemptions
  automod_exempt_roles: string;
  automod_exempt_channels: string;
  // Logging
  automod_log_channel_id: string | null;
  automod_dm_users: boolean;
  // Timeout duration (minutes)
  automod_timeout_duration: number;
  // Warning escalation system
  automod_warn_threshold: number;
  automod_warn_action: 'timeout' | 'kick' | 'ban';
}

export interface AutomodSettingsUpdate {
  automod_enabled?: number;
  automod_links_enabled?: number;
  automod_links_action?: AutomodAction;
  automod_links_whitelist?: string | null;
  automod_invites_enabled?: number;
  automod_invites_action?: AutomodAction;
  automod_invites_whitelist?: string | null;
  automod_mentions_enabled?: number;
  automod_mentions_limit?: number;
  automod_mentions_action?: AutomodAction;
  automod_spam_enabled?: number;
  automod_spam_action?: AutomodAction;
  automod_words_enabled?: number;
  automod_words_list?: string | null;
  automod_words_action?: AutomodAction;
  automod_words_wildcard?: number;
  automod_zalgo_enabled?: number;
  automod_zalgo_action?: AutomodAction;
  automod_preset_enabled?: number;
  automod_preset_profanity?: number;
  automod_preset_slurs?: number;
  automod_preset_sexual?: number;
  automod_exempt_roles?: string | null;
  automod_exempt_channels?: string | null;
  automod_log_channel_id?: string | null;
  automod_dm_users?: number;
  automod_timeout_duration?: number;
  automod_warn_threshold?: number;
  automod_warn_action?: 'timeout' | 'kick' | 'ban';
}

export interface WebhookSettings {
  url: string | null;
  events: string[];
  embed_color: string;
  footer_text: string | null;
  author_name: string | null;
  thumbnail_enabled: boolean;
  timestamp_enabled: boolean;
}

export interface WebhookSettingsUpdate {
  webhook_url?: string | null;
  webhook_events?: string;
  embed_color?: string;
  footer_text?: string | null;
  author_name?: string | null;
  thumbnail_enabled?: boolean;
  timestamp_enabled?: boolean;
}

export interface WelcomerEmbedSettings {
  enabled: boolean;
  title: string | null;
  color: string;
  thumbnail_type: 'user_avatar' | 'server_icon' | 'custom' | 'none';
  thumbnail_url: string | null;
  image_url: string | null;
  footer_text: string | null;
  footer_icon_url: string | null;
  author_name: string | null;
  author_icon_url: string | null;
  timestamp_enabled: boolean;
}

export interface WelcomerSettings {
  enabled: boolean;
  channel_id: string | null;
  message: string | null;
  embed: WelcomerEmbedSettings;
}

export interface WelcomerSettingsUpdate {
  welcomer_enabled?: number;
  welcomer_channel_id?: string | null;
  welcomer_message?: string | null;
  welcomer_embed_enabled?: number;
  welcomer_embed_title?: string | null;
  welcomer_embed_color?: string;
  welcomer_embed_thumbnail_type?: 'user_avatar' | 'server_icon' | 'custom' | 'none';
  welcomer_embed_thumbnail_url?: string | null;
  welcomer_embed_image_url?: string | null;
  welcomer_embed_footer_text?: string | null;
  welcomer_embed_footer_icon_url?: string | null;
  welcomer_embed_author_name?: string | null;
  welcomer_embed_author_icon_url?: string | null;
  welcomer_embed_timestamp_enabled?: number;
}

export interface GeneralSettings {
  notifications: {
    dm_on_verify: boolean;
    dm_on_block: boolean;
    quiet_mode: boolean;
  };
  verification: {
    timeout: number;
    max_attempts: number;
    auto_kick_on_fail: boolean;
    allow_reverification: boolean;
  };
  log_channel_id: string | null;
  setup: {
    completed: boolean;
    channel_id: string | null;
    role_id: string | null;
    message_id: string | null;
  };
}

export interface GeneralSettingsUpdate {
  dm_on_verify?: number;
  dm_on_block?: number;
  quiet_mode?: number;
  verification_timeout?: number;
  max_verification_attempts?: number;
  auto_kick_on_fail?: number;
  allow_reverification?: number;
  log_channel_id?: string | null;
}

export interface SetupInfo {
  completed: boolean;
  verification_channel_id: string | null;
  verification_role_id: string | null;
  verification_message_id: string | null;
}

export interface DiscordChannel {
  id: string;
  name: string;
  parent_id: string | null;
}

export interface DiscordRole {
  id: string;
  name: string;
  color: string | null;
}

export interface CustomMessages {
  custom_verification_message: string | null;
  custom_success_message: string | null;
  custom_blocked_message: string | null;
  custom_verification_footer: string | null;
}

export interface SecurityBlock {
  id: number;
  guild_id: string;
  user_id: string;
  ip_address: string | null;
  block_reason: string;
  country_code: string | null;
  is_vpn: number;
  is_proxy: number;
  is_tor: number;
  blocked_at: string;
}

export interface VerifiedMember {
  id: number;
  user_id: string;
  guild_id: string;
  ip_address: string | null;
  country_code: string | null;
  verification_method: string;
  verified_at: string;
}

export interface AdminStats {
  users: { total: number; new_7d: number };
  guilds: { total: number; new_7d: number };
  verifications: { total: number; recent_24h: number };
  blocks: {
    total: number;
    recent_24h: number;
    breakdown: { vpn: number; proxy: number; tor: number };
  };
}

export interface DetailedStats {
  // Local DB stats
  guilds: number;
  verified_members: number;
  security_blocks: number;
  active_api_keys_local: number;
  // Main API stats
  cached_ips: number;
  api_keys_count: number;
  whitelist_count: number;
  blacklist_count: number;
  usage_all_time: {
    total_requests: number;
    cached_hits: number;
  };
  usage_today: {
    total_requests: number;
    cached_hits: number;
  };
  top_countries: Array<{ country: string; count: number }>;
}

export interface AdminProduct {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  short_description: string | null;
  price: number;
  compare_price: number | null;
  currency: string;
  category: string;
  type: string;
  duration_days: number | null;
  features: string[];
  image_url: string | null;
  is_active: number;
  is_featured: number;
  sort_order: number;
  stock: number | null;
  created_at: string;
  updated_at: string;
}

export interface UserSession {
  id: number;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  last_used_at: string | null;
  expires_at: string;
}

export interface AdminPurchase {
  id: number;
  user_id: number;
  user_name: string | null;
  user_email: string | null;
  product_id: number;
  product_name: string;
  category: string;
  type: string;
  order_id: number | null;
  order_number: string | null;
  guild_id: string | null;
  status: string;
  quantity: number;
  expires_at: string | null;
  created_at: string;
}

export interface AdminUser {
  id: number;
  name: string;
  email: string | null;
  discord_id: string | null;
  avatar: string | null;
  two_factor_enabled: number;
  email_verified: number;
  created_at: string;
  last_login_at: string | null;
}

export interface AdminUserDetails extends AdminUser {
  preferred_language: string;
  updated_at: string;
  providers: { provider: string; provider_user_id: string; created_at: string }[];
  roles: { name: string; display_name: string; guild_id: string | null }[];
  recent_activity: ActivityLog[];
}

export interface ActivityLog {
  id: number;
  user_id: number | null;
  guild_id: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  details: string | null;
  ip_address: string | null;
  created_at: string;
  user_name?: string;
  user_email?: string;
  user_avatar?: string | null;
}

export interface SecurityDetection {
  id: number;
  guild_id: string;
  user_id: string;
  ip_address: string | null;
  is_vpn: number;
  is_proxy: number;
  is_tor: number;
  country_code: string | null;
  isp: string | null;
  blocked_at: string;
  guild_name: string | null;
}

export interface Role {
  id: number;
  name: string;
  display_name: string;
  permissions: string;
  created_at: string;
}

export interface Language {
  code: string;
  name: string;
  native_name: string;
}

export interface EmailLog {
  id: number;
  template_slug: string | null;
  recipient: string;
  recipient_name: string | null;
  subject: string;
  status: string;
  mailgun_message_id: string | null;
  error: string | null;
  metadata: string | null;
  html_body: string | null;
  text_body: string | null;
  queued_at: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  created_at: string;
}

export interface EmailQueueItem {
  id: number;
  recipient: string;
  subject: string;
  status: string;
  priority: number;
  attempts: number;
  error: string | null;
  created_at: string;
  processed_at: string | null;
}

export interface EmailTemplate {
  id: number;
  slug: string;
  name: string;
  subject: string;
  html_body: string;
  text_body: string | null;
  variables: string;
  description: string | null;
  is_active: number;
  primary_color: string;
  secondary_color: string;
  button_color: string;
  button_text_color: string;
  header_icon: string | null;
  logo_url: string | null;
  footer_text: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContributionGoal {
  id: number;
  title: string;
  description: string | null;
  target_amount: number;
  current_amount: number;
  start_date: string;
  end_date: string;
  progress_percentage: number;
}

export interface ContributionEntry {
  id: number;
  name: string;
  amount: number;
  message: string | null;
  created_at: string;
}

export interface ShopProduct {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  short_description: string | null;
  price: number;
  compare_price: number | null;
  currency: string;
  category: string;
  type: string;
  duration_days: number | null;
  features: string[];
  image_url: string | null;
  is_active: number;
  is_featured: number;
  sort_order: number;
  stock: number | null;
}

export interface ShopOrder {
  id: number;
  order_number: string;
  status: string;
  total_amount: number;
  currency: string;
  created_at: string;
  billing_email?: string;
}

export interface ShopOrderItem {
  id: number;
  order_id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export interface UserPurchase {
  id: number;
  user_id: number;
  product_id: number;
  order_id: number | null;
  discord_id: string | null;
  guild_id: string | null;
  status: string;
  quantity: number;
  expires_at: string | null;
  activated_at: string;
  product_name: string;
  product_slug: string;
  category: string;
  type: string;
  features: string[];
  description: string | null;
  price: number;
  image_url: string | null;
  order_number: string | null;
}

export interface IpCheckResult {
  ip: string;
  discord_user_id: string | null;
  is_vpn: boolean;
  is_proxy: boolean;
  is_tor: boolean;
  is_datacenter: boolean;
  is_blacklisted: boolean;
  is_whitelisted: boolean;
  risk_score: number | null;
  country_code: string | null;
  country: string | null;
  isp: string | null;
  city: string | null;
  threat_types: string[];
}

export interface Entitlements {
  premium: boolean;
  custom_branding: boolean;
  custom_messages: boolean;
  analytics: boolean;
  webhooks: boolean;
  no_branding: boolean;
  extra_slots: number;
}

export interface VerificationInfo {
  code: string;
  guild_id: string;
  guild_name: string;
  guild_icon: string | null;
  custom_message: string;
  blocked: boolean;
  block_reason: string | null;
  custom_blocked_message: string | null;
}

export interface UserActivityLog {
  id: number;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  ip_address: string | null;
  created_at: string;
}

export interface NotificationSettings {
  email_notifications: boolean;
  security_alerts: boolean;
  marketing_emails: boolean;
  guild_activity: boolean;
  verification_alerts: boolean;
  system_updates: boolean;
  weekly_reports: boolean;
  login_alerts: boolean;
  api_usage_alerts: boolean;
}

export interface ServiceStatus {
  name: string;
  status: 'operational' | 'degraded' | 'outage';
  uptime: number;
  response_time: number;
  last_checked: string;
}

export interface StatusIncident {
  id: number;
  title: string;
  description: string;
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved';
  severity: 'minor' | 'major' | 'critical';
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

export interface StatusData {
  overall_status: 'operational' | 'degraded' | 'outage';
  services: ServiceStatus[];
  incidents: StatusIncident[];
  uptime_30d: number;
}

export interface AdminLanguage {
  code: string;
  name: string;
  native_name: string;
  enabled: number;
}

export interface AdminApiKeyInfo {
  user_id: string;
  api_key: string;
  usage_count: number;
  created_at: string;
  last_used_at: string | null;
  active: number;
  user_name?: string;
  user_email?: string;
}

export interface AppKeyPermissions {
  users?: { read: boolean; write: boolean };
  guilds?: { read: boolean; write: boolean };
  verified_members?: { read: boolean; write: boolean };
  security_blocks?: { read: boolean; write: boolean };
  activity_logs?: { read: boolean; write: boolean };
}

export interface ApplicationApiKey {
  id: number;
  name: string;
  description: string | null;
  key_prefix: string;
  permissions: AppKeyPermissions;
  allowed_ips: string | null;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ThreatStatsResponse {
  threats_detected: number;
  vpn_detected: number;
  tor_detected: number;
  datacenter_detected: number;
  abuser_detected: number;
  proxy_detected: number;
  threats_blocked_24h: number;
  vpn_blocked_24h: number;
  tor_blocked_24h: number;
  datacenter_blocked_24h: number;
  abuser_blocked_24h: number;
  proxy_blocked_24h: number;
  total_cached_ips: number;
  total_reputation_ips: number;
  total_unique_ips: number;
  recent_threats: Array<{
    ip: string;
    threat_types: string[];
    country: string;
    country_code: string;
    detected_at: string;
  }>;
  timestamp: string;
}

// ============================================
// ROLE MANAGEMENT TYPES
// ============================================

export interface JoinRole {
  id: number;
  role_id: string;
}

export interface ButtonRole {
  id?: number;
  role_id: string;
  label?: string;
  emoji?: string;
  style?: 'primary' | 'secondary' | 'success' | 'danger';
}

export interface ButtonRolePanel {
  id: number;
  guild_id: string;
  channel_id: string;
  message_id: string | null;
  title: string;
  description: string;
  color: string;
  style: 'buttons' | 'select_menu';
  max_roles: number;
  roles: ButtonRole[];
  created_at: string;
  updated_at: string;
}

export interface ButtonRolePanelCreate {
  channel_id: string;
  title?: string;
  description?: string;
  color?: string;
  style?: 'buttons' | 'select_menu';
  max_roles?: number;
  roles?: ButtonRole[];
}

export const api = new ApiClient(API_URL);
